import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConversations } from "../../api/chat";
import { useMe } from "../../api/hooks";
import { ChatView } from "../../components/chat/ChatView";
import { t } from "../../i18n";
import { jumpState } from "../../lib/jump";

type Search = { thread?: string; m?: string };

export const Route = createFileRoute("/_app/ch/$slug")({
  // `thread`: the open thread panel; `m`: a linked message (PLAN §6.5).
  validateSearch: (search: Record<string, unknown>): Search => ({
    ...(typeof search.thread === "string" && search.thread ? { thread: search.thread } : {}),
    ...(typeof search.m === "string" && search.m ? { m: search.m } : {}),
  }),
  component: ChannelPage,
});

function ChannelPage() {
  const { slug } = Route.useParams();
  const { thread, m } = Route.useSearch();
  const navigate = useNavigate();
  const me = useMe();
  const conversations = useConversations();
  const channel = (conversations.data ?? []).find((c) => c.kind === "channel" && c.slug === slug);
  if (!me.data || conversations.isLoading) return null;
  if (!channel) {
    return (
      <main className="sub main on no-panel" aria-label={t("chat.channel")}>
        <div className="page">
          <div className="empty">
            <h2>{t("chat.channelGone")}</h2>
            <p>{t("chat.channelGoneBody")}</p>
          </div>
        </div>
      </main>
    );
  }
  return (
    <ChatView
      key={channel.id}
      conversationId={channel.id}
      me={me.data}
      thread={thread ?? null}
      jumpTo={m ?? null}
      onThread={(root) =>
        void navigate({
          to: "/ch/$slug",
          params: { slug },
          search: root ? { thread: root } : {},
          replace: Boolean(thread && root),
        })
      }
      onTarget={(root, message, push) =>
        void navigate({
          to: "/ch/$slug",
          params: { slug },
          search: { ...(root ? { thread: root } : {}), ...(message ? { m: message } : {}) },
          // A search hit is a followed link: it goes to its message again even when that's the
          // one open (R10-B1), replacing the entry then (nothing new to go Back to).
          replace: !push || ((root ?? undefined) === thread && (message ?? undefined) === m),
          ...(push ? { state: jumpState() } : {}),
        })
      }
    />
  );
}
